import React, { useState, useMemo } from 'react';
import Plot from 'react-plotly.js';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import {
  ChevronUpDownIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// CONFIG - Ohio DOT PN 420 Surface Smoothness Requirements
// =============================================================================

const LOT_LENGTH = 528; // feet (0.1 mile)
const ALR_THRESHOLD = 160; // in/mi - localized roughness threshold
const ALR_INTERVAL = 25; // feet - continuous interval for ALR check

// Note: Ohio uses MRI (Mean Roughness Index) - average of left/right wheel paths
// This is the same as averaging the two wheel path IRIs

const SCHEDULES = {
  THIN: {
    id: 'THIN',
    label: 'Less than 8"',
    description: 'Total pavement thickness < 8 inches',
    tiers: [
      { max: 45,       adjustment: 375,   tier: 'incentive', label: '≤ 45' },
      { max: 50,       adjustment: 225,   tier: 'incentive', label: '> 45 to 50' },
      { max: 55,       adjustment: 150,   tier: 'incentive', label: '> 50 to 55' },
      { max: 60,       adjustment: 75,    tier: 'incentive', label: '> 55 to 60' },
      { max: 70,       adjustment: 0,     tier: 'base',      label: '> 60 to 70' },
      { max: 75,       adjustment: -150,  tier: 'penalty',   label: '> 70 to 75' },
      { max: 80,       adjustment: -300,  tier: 'penalty',   label: '> 75 to 80' },
      { max: 85,       adjustment: -450,  tier: 'penalty',   label: '> 80 to 85' },
      { max: 90,       adjustment: -600,  tier: 'penalty',   label: '> 85 to 90' },
      { max: 95,       adjustment: -750,  tier: 'penalty',   label: '> 90 to 95' },
      { max: Infinity, adjustment: 0,     tier: 'corrective', label: '> 95', requiresCorrection: true },
    ],
    correctionThreshold: 95,
  },
  THICK: {
    id: 'THICK',
    label: '8" or Greater',
    description: 'Total pavement thickness ≥ 8 inches',
    tiers: [
      { max: 45,       adjustment: 875,   tier: 'incentive', label: '≤ 45' },
      { max: 50,       adjustment: 525,   tier: 'incentive', label: '> 45 to 50' },
      { max: 55,       adjustment: 350,   tier: 'incentive', label: '> 50 to 55' },
      { max: 60,       adjustment: 175,   tier: 'incentive', label: '> 55 to 60' },
      { max: 70,       adjustment: 0,     tier: 'base',      label: '> 60 to 70' },
      { max: 75,       adjustment: -350,  tier: 'penalty',   label: '> 70 to 75' },
      { max: 80,       adjustment: -700,  tier: 'penalty',   label: '> 75 to 80' },
      { max: 85,       adjustment: -1050, tier: 'penalty',   label: '> 80 to 85' },
      { max: 90,       adjustment: -1400, tier: 'penalty',   label: '> 85 to 90' },
      { max: 95,       adjustment: -1750, tier: 'penalty',   label: '> 90 to 95' },
      { max: Infinity, adjustment: 0,     tier: 'corrective', label: '> 95', requiresCorrection: true },
    ],
    correctionThreshold: 95,
  },
};

const DEFAULT_SETTINGS = {
  schedule: 'THIN',
};

// =============================================================================
// CALCULATION HELPERS
// =============================================================================

const getAdjustmentTier = (iri, schedule) => {
  const sched = SCHEDULES[schedule];
  for (const tier of sched.tiers) {
    if (iri <= tier.max || tier.max === Infinity) {
      return {
        adjustment: tier.adjustment,
        tier: tier.tier,
        label: tier.label,
        requiresCorrection: tier.requiresCorrection || false,
      };
    }
  }
  // Fallback - should not reach here
  const lastTier = sched.tiers[sched.tiers.length - 1];
  return {
    adjustment: 0,
    tier: lastTier.tier,
    label: lastTier.label,
    requiresCorrection: true,
  };
};

// =============================================================================
// ALR HELPERS
// =============================================================================

const getALRsInSegment = (segmentStart, segmentEnd, alrDefects) => {
  const minSta = Math.min(segmentStart, segmentEnd);
  const maxSta = Math.max(segmentStart, segmentEnd);
  
  return (alrDefects || []).filter(defect => {
    // ALR defects have startDistance/stopDistance or distance/station
    const defectStart = defect.startDistance ?? defect.distance ?? defect.station ?? 0;
    const defectStop = defect.stopDistance ?? defectStart;
    
    // Check if defect overlaps with segment
    const defectMin = Math.min(defectStart, defectStop);
    const defectMax = Math.max(defectStart, defectStop);
    
    // Overlap if defect range intersects segment range
    return defectMax >= minSta && defectMin <= maxSta;
  });
};

// =============================================================================
// MAIN CALCULATION
// =============================================================================

const calculate = (segments, alrDefects, settings, isReversed) => {
  if (!segments?.length) return null;
  
  const validSegments = segments
    .filter(seg => !seg.exception)
    .map((seg) => ({
      ...seg,
      startStation: seg.from_station_ft || 0,
      stopStation: seg.to_station_ft || 0,
    }));
  
  if (validSegments.length === 0) return null;
  
  const sortedSegments = [...validSegments].sort((a, b) => 
    isReversed 
      ? b.startStation - a.startStation
      : a.startStation - b.startStation
  );
  
  let totalAdjustment = 0;
  let totalIncentive = 0;
  let totalPenalty = 0;
  let totalDistance = 0;
  let mriSum = 0;
  let correctiveCount = 0;
  let totalALRCount = 0;
  
  // Tier counts for summary
  const sched = SCHEDULES[settings.schedule];
  const tierCounts = {};
  sched.tiers.forEach(t => {
    if (!t.requiresCorrection) {
      tierCounts[t.adjustment] = 0;
    }
  });
  
  const rows = sortedSegments.map((seg, i) => {
    const mri = seg.iri_avg_in_per_mi || 0; // MRI is the average of left/right wheel paths
    const segmentLength = Math.abs(seg.stopStation - seg.startStation);
    const prorationFactor = Math.min(segmentLength / LOT_LENGTH, 1.0);
    const isPartialLot = segmentLength < LOT_LENGTH;
    
    // Get MRI-based adjustment tier
    const tierResult = getAdjustmentTier(mri, settings.schedule);
    
    // Count ALRs in this segment
    const alrsInSegment = getALRsInSegment(seg.startStation, seg.stopStation, alrDefects);
    const alrCount = alrsInSegment.length;
    totalALRCount += alrCount;
    
    // Check if segment requires correction (MRI > 95 or has ALRs)
    const requiresCorrectionMRI = tierResult.requiresCorrection;
    const requiresCorrectionALR = alrCount > 0;
    const requiresCorrection = requiresCorrectionMRI || requiresCorrectionALR;
    
    // Always calculate prorated adjustment based on MRI
    // For MRI >95, adjustment is 0 (corrective tier)
    // For ALR-triggered correction, show what adjustment would be if ALR waived
    const proratedAdjustment = tierResult.adjustment * prorationFactor;
    
    // Always count tiers based on MRI (regardless of ALR correction)
    // This shows the distribution of what lots WOULD pay based on smoothness
    if (tierResult.adjustment in tierCounts) {
      tierCounts[tierResult.adjustment] += prorationFactor;
    }
    
    // Only add to totals if no correction required
    if (!requiresCorrection) {
      totalAdjustment += proratedAdjustment;
      
      if (proratedAdjustment > 0) {
        totalIncentive += proratedAdjustment;
      } else if (proratedAdjustment < 0) {
        totalPenalty += proratedAdjustment;
      }
    }
    
    // Track corrective count
    if (requiresCorrection) correctiveCount++;
    
    totalDistance += segmentLength;
    mriSum += mri;
    
    const displayStart = isReversed ? seg.stopStation : seg.startStation;
    const displayStop = isReversed ? seg.startStation : seg.stopStation;
    
    return {
      num: i + 1,
      segmentId: seg.segment_id,
      mri,
      startStation: displayStart,
      stopStation: displayStop,
      length: segmentLength,
      prorationFactor,
      isPartialLot,
      baseAdjustment: tierResult.adjustment,
      proratedAdjustment,
      tierLabel: tierResult.label,
      tier: tierResult.tier,
      requiresCorrection,
      requiresCorrectionMRI,
      requiresCorrectionALR,
      alrCount,
    };
  });
  
  const avgMRI = rows.length > 0 ? mriSum / rows.length : 0;
  
  return {
    rows,
    totalAdjustment,
    totalIncentive,
    totalPenalty,
    avgMRI,
    totalDistance,
    segmentCount: rows.length,
    correctiveCount,
    totalALRCount,
    tierCounts,
  };
};

// =============================================================================
// HELPERS
// =============================================================================

const cn = (...c) => c.filter(Boolean).join(' ');

const fmtStation = (ft) => {
  if (ft == null) return '—';
  const hundreds = Math.floor(ft / 100);
  const remainder = Math.round(ft % 100);
  return `${hundreds}+${String(remainder).padStart(2, '0')}`;
};

const fmtCurrency = (v) => {
  const formatted = Math.abs(v).toLocaleString('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    minimumFractionDigits: 2 
  });
  return v < 0 ? `-${formatted}` : formatted;
};

// =============================================================================
// COPY VALUE COMPONENT
// =============================================================================

const CopyValue = ({ label, value, displayValue, className, valueClassName }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div className={cn('flex items-center justify-between py-1.5', className)}>
      <span className="text-gray-600 text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <span className={cn('font-mono text-sm font-semibold text-gray-900', valueClassName)}>
          {displayValue ?? value}
        </span>
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-gray-100 transition-colors"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// COPYABLE CELL COMPONENT
// =============================================================================

const CopyableCell = ({ value, displayValue, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <td className={cn('px-3 py-2 group', className)}>
      <div className="flex items-center gap-1">
        <span>{displayValue ?? value}</span>
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-3 w-3 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </td>
  );
};

// =============================================================================
// COPYABLE COLUMN HEADER
// =============================================================================

const CopyableHeader = ({ label, data, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(data.join('\r\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <th className={cn('px-3 py-2 text-left group', className)}>
      <span>{label}</span>
      <button
        onClick={handleCopy}
        className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
        title={`Copy ${label} column`}
      >
        {copied ? (
          <CheckIcon className="h-3.5 w-3.5 text-green-500 inline" />
        ) : (
          <ClipboardDocumentIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 inline" />
        )}
      </button>
    </th>
  );
};

// =============================================================================
// PAY TABLE MODAL
// =============================================================================

const PayTableModal = ({ open, onClose, schedule }) => {
  const sched = SCHEDULES[schedule];
  
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <DialogPanel className="bg-white rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
            <DialogTitle className="text-base font-semibold text-gray-900">
              Ohio DOT Pay Adjustment Table
            </DialogTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-4 space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <div className="font-semibold text-blue-800 mb-1">Current Configuration</div>
              <div className="text-blue-700">
                Pavement Thickness: {sched.label} — {sched.description}
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                PN 420 Pay Schedule — Adjustment per 0.1 Mile Segment
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {/* Thin Schedule */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 mb-1 uppercase">Less than 8"</h4>
                  <table className="w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="px-2 py-1.5 text-left border-b">MRI (in/mi)</th>
                        <th className="px-2 py-1.5 text-right border-b">Adjustment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {SCHEDULES.THIN.tiers.map((tier, i) => (
                        <tr key={i} className={cn(
                          tier.requiresCorrection ? 'bg-red-50' : 
                          tier.adjustment > 0 ? 'bg-green-50' : 
                          tier.adjustment < 0 ? 'bg-yellow-50' : ''
                        )}>
                          <td className="px-2 py-1.5 text-gray-700 text-xs">{tier.label}</td>
                          <td className={cn(
                            'px-2 py-1.5 font-mono text-right text-xs',
                            tier.requiresCorrection ? 'text-red-700' : 
                            tier.adjustment > 0 ? 'text-green-700' : 
                            tier.adjustment < 0 ? 'text-red-600' : 'text-gray-500'
                          )}>
                            {tier.requiresCorrection ? 'Correct' : fmtCurrency(tier.adjustment)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Thick Schedule */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 mb-1 uppercase">8" or Greater</h4>
                  <table className="w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                        <th className="px-2 py-1.5 text-left border-b">MRI (in/mi)</th>
                        <th className="px-2 py-1.5 text-right border-b">Adjustment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {SCHEDULES.THICK.tiers.map((tier, i) => (
                        <tr key={i} className={cn(
                          tier.requiresCorrection ? 'bg-red-50' : 
                          tier.adjustment > 0 ? 'bg-green-50' : 
                          tier.adjustment < 0 ? 'bg-yellow-50' : ''
                        )}>
                          <td className="px-2 py-1.5 text-gray-700 text-xs">{tier.label}</td>
                          <td className={cn(
                            'px-2 py-1.5 font-mono text-right text-xs',
                            tier.requiresCorrection ? 'text-red-700' : 
                            tier.adjustment > 0 ? 'text-green-700' : 
                            tier.adjustment < 0 ? 'text-red-600' : 'text-gray-500'
                          )}>
                            {tier.requiresCorrection ? 'Correct' : fmtCurrency(tier.adjustment)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-yellow-800 mb-1">Localized Roughness (ALR)</div>
                  <div className="text-yellow-700 text-xs">
                    Any area with MRI exceeding {ALR_THRESHOLD} in/mi over {ALR_INTERVAL} ft continuous 
                    requires corrective work regardless of segment average MRI.
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-orange-50 border border-orange-200 rounded text-sm">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-orange-800 mb-1">No Incentive After Correction</div>
                  <div className="text-orange-700 text-xs">
                    If mandatory corrective work was performed on a segment, no incentive will be paid 
                    regardless of the resulting MRI. Disincentives still apply.
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 rounded text-xs text-gray-600 space-y-1">
              <p className="font-semibold">Reference</p>
              <p>Ohio DOT Proposal Note 420 — Surface Smoothness Requirements for Pavements</p>
              <p className="mt-2">
                <strong>MRI (Mean Roughness Index):</strong> Average of left and right wheel path IRI values.
              </p>
              <p className="mt-1">
                <strong>Pavement Thickness:</strong> Total thickness of asphalt concrete, Portland cement concrete, 
                or both placed as part of the contract. Does not include free draining base, aggregate base, 
                or stabilized subgrade.
              </p>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function OhioPayAdjustment({ project, segments, alrDefects, isReversed = false }) {
  const [settings, setSettings] = useState(() => ({ 
    ...DEFAULT_SETTINGS, 
    ...project?.payAdjustment,
  }));
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);
  
  const data = useMemo(
    () => calculate(segments, alrDefects, settings, isReversed), 
    [segments, alrDefects, settings, isReversed]
  );
  
  const displayRows = useMemo(() => {
    if (!data) return [];
    return issuesOnly ? data.rows.filter(r => r.requiresCorrection || r.tier === 'penalty') : data.rows;
  }, [data, issuesOnly]);
  
  const sched = SCHEDULES[settings.schedule];
  
  const exportCSV = () => {
    const headers = [
      'Lot',
      'Beginning Sta.',
      'Ending Sta.',
      'Length (ft)',
      'MRI (in/mi)',
      'ALR Count',
      'Tier',
      'Base Adjustment',
      'Prorated Adjustment',
      'Requires Correction',
      'Correction Reason'
    ].join(',');
    
    const csvRows = displayRows.map(r => [
      r.num,
      r.startStation.toFixed(0),
      r.stopStation.toFixed(0),
      r.length.toFixed(0),
      r.mri.toFixed(1),
      r.alrCount,
      r.tierLabel,
      r.baseAdjustment.toFixed(2),
      r.proratedAdjustment.toFixed(2),
      r.requiresCorrection ? 'Yes' : 'No',
      r.requiresCorrectionMRI ? 'MRI' : r.requiresCorrectionALR ? 'ALR' : '',
    ].join(','));
    
    const blob = new Blob([[headers, ...csvRows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ohio-pn420-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  
  // Count issues for filter toggle
  const issueCount = data ? data.rows.filter(r => r.requiresCorrection || r.tier === 'penalty').length : 0;
  
  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      <PayTableModal open={showPayTable} onClose={() => setShowPayTable(false)} schedule={settings.schedule} />
      
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <div className="mr-2">
          <h3 className="text-sm font-semibold text-gray-900">Profile Summary Sheet</h3>
          <p className="text-xs text-gray-500">Ohio DOT PN 420</p>
        </div>
        
        <div className="h-8 w-px bg-gray-300" />
        
        {/* Thickness Schedule Selector */}
        <Listbox value={settings.schedule} onChange={(v) => setSettings(s => ({ ...s, schedule: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left whitespace-nowrap">
              Thickness: {sched.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
              {Object.values(SCHEDULES).map(s => (
                <ListboxOption 
                  key={s.id} 
                  value={s.id} 
                  className={({ active }) => cn('px-3 py-2 cursor-pointer whitespace-nowrap', active && 'bg-blue-50')}
                >
                  <span className="font-medium">{s.label}</span>
                  <span className="text-gray-500 ml-2 text-xs">{s.description}</span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        <div className="flex-1" />
        
        <button 
          onClick={() => setShowPayTable(true)} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <InformationCircleIcon className="h-4 w-4" />
          Pay Table
        </button>
        
        <button 
          onClick={exportCSV} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export
        </button>
      </div>
      
      {/* Summary */}
      {data ? (
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex gap-4">
            {/* Left: Tier Distribution Bar Chart */}
            <div className="flex-1 border border-gray-200 rounded overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700">Pay Adjustment Distribution</h4>
              </div>
              <div className="p-2">
                {(() => {
                  // Build chart data from tiers (sorted from lowest disincentive to highest incentive)
                  const allTiers = sched.tiers
                    .filter(t => !t.requiresCorrection)
                    .sort((a, b) => a.adjustment - b.adjustment);
                  
                  const labels = allTiers.map(t => {
                    if (t.adjustment === 0) return '$0';
                    if (t.adjustment > 0) return `+$${t.adjustment}`;
                    return `-$${Math.abs(t.adjustment)}`;
                  });
                  
                  const values = allTiers.map(t => data.tierCounts[t.adjustment] || 0);
                  
                  const colors = allTiers.map(t => {
                    if (t.adjustment > 0) return '#22c55e'; // green
                    if (t.adjustment < 0) return '#ef4444'; // red
                    return '#9ca3af'; // gray for $0
                  });

                  return (
                    <Plot
                      data={[{
                        type: 'bar',
                        x: labels,
                        y: values,
                        marker: {
                          color: colors,
                        },
                        text: values.map(v => v > 0 ? (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)) : ''),
                        textposition: 'outside',
                        textfont: { size: 10 },
                        hovertemplate: '%{x}: %{y:.2f} lots<extra></extra>'
                      }]}
                      layout={{
                        height: 160,
                        margin: { t: 10, r: 10, b: 40, l: 35 },
                        xaxis: {
                          tickangle: -45,
                          tickfont: { size: 9 },
                          fixedrange: true
                        },
                        yaxis: {
                          title: { text: 'Lots', font: { size: 10 } },
                          tickfont: { size: 9 },
                          fixedrange: true,
                          rangemode: 'tozero'
                        },
                        bargap: 0.3,
                        plot_bgcolor: 'white',
                        paper_bgcolor: 'white'
                      }}
                      config={{
                        displayModeBar: false,
                        staticPlot: false
                      }}
                      style={{ width: '100%', height: '160px' }}
                      useResizeHandler={true}
                    />
                  );
                })()}
              </div>
            </div>
            
            {/* Right: Totals */}
            <div className="w-64 border border-gray-200 rounded overflow-hidden flex-shrink-0">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700">Totals</h4>
              </div>
              <div className="px-3 py-2 divide-y divide-gray-100">
                <CopyValue 
                  label="Net Adjustment"
                  value={data.totalAdjustment.toFixed(2)}
                  displayValue={fmtCurrency(data.totalAdjustment)}
                  valueClassName={data.totalAdjustment >= 0 ? 'text-green-600' : 'text-red-600'}
                />
                <CopyValue 
                  label="Total Incentive"
                  value={data.totalIncentive.toFixed(2)}
                  displayValue={fmtCurrency(data.totalIncentive)}
                  valueClassName="text-green-600"
                />
                <CopyValue 
                  label="Total Disincentive"
                  value={data.totalPenalty.toFixed(2)}
                  displayValue={fmtCurrency(data.totalPenalty)}
                  valueClassName={data.totalPenalty < 0 ? 'text-red-600' : ''}
                />
                <CopyValue 
                  label="Average MRI"
                  value={data.avgMRI.toFixed(1)}
                  displayValue={`${data.avgMRI.toFixed(1)} in/mi`}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-gray-400 border-b border-gray-200">
          No segment data available
        </div>
      )}
      
      {/* Segments Header */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Lots {data && <span className="text-gray-400 font-normal">({data.rows.length})</span>}
        </span>
        {issueCount > 0 && (
          <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={issuesOnly} 
              onChange={e => setIssuesOnly(e.target.checked)} 
              className="w-3.5 h-3.5 text-blue-600 rounded" 
            />
            Show issues only ({issueCount})
          </label>
        )}
      </div>
      
      {/* Table */}
      <div className="flex-1 overflow-auto">
        {displayRows.length > 0 ? (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left w-12 border-r border-gray-200">Lot</th>
                <th className="px-3 py-2 text-left border-r border-gray-200">Beginning Sta.</th>
                <th className="px-3 py-2 text-left border-r border-gray-200">Ending Sta.</th>
                <th className="px-3 py-2 text-left border-r border-gray-200">Length (ft)</th>
                <CopyableHeader label="MRI (in/mi)" data={displayRows.map(r => r.mri.toFixed(1))} className="border-r border-gray-200" />
                <th className="pl-5 pr-3 py-2 text-left whitespace-nowrap border-r border-gray-200">ALRs</th>
                <CopyableHeader 
                  label="Lot Adjustment" 
                  data={displayRows.map(r => r.proratedAdjustment.toFixed(2))} 
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayRows.map(r => (
                <tr key={r.num} className={cn(
                  'hover:bg-gray-50',
                  r.requiresCorrection && 'bg-red-50',
                  r.tier === 'penalty' && !r.requiresCorrection && 'bg-yellow-50'
                )}>
                  <td className="px-3 py-2 text-gray-400 text-xs border-r border-gray-200">{r.num}</td>
                  <CopyableCell 
                    value={r.startStation.toFixed(0)} 
                    displayValue={fmtStation(r.startStation)}
                    className="text-gray-700 font-mono text-xs border-r border-gray-200"
                  />
                  <CopyableCell 
                    value={r.stopStation.toFixed(0)} 
                    displayValue={fmtStation(r.stopStation)}
                    className="text-gray-700 font-mono text-xs border-r border-gray-200"
                  />
                  <CopyableCell 
                    value={r.length.toFixed(0)} 
                    displayValue={r.length.toFixed(0)}
                    className="text-gray-700 border-r border-gray-200"
                  />
                  <td className={cn(
                    'px-3 py-2 text-left border-r border-gray-200',
                    r.requiresCorrectionMRI ? 'text-red-600 font-semibold' : 
                    r.tier === 'penalty' ? 'text-yellow-700' : 'text-gray-900'
                  )}>
                    {r.mri.toFixed(1)}
                  </td>
                  <td className="pl-5 pr-3 py-2 text-left border-r border-gray-200">
                    {r.alrCount > 0 ? (
                      <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold bg-red-100 text-red-700 rounded">
                        {r.alrCount}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-left">
                    {r.requiresCorrection ? (
                      <div className="flex items-center gap-1.5">
                        <span className={cn(
                          'font-mono',
                          r.proratedAdjustment > 0 ? 'text-green-600' : 
                          r.proratedAdjustment < 0 ? 'text-red-600' : 'text-gray-500'
                        )}>
                          {fmtCurrency(r.proratedAdjustment)}
                        </span>
                        {r.isPartialLot && (
                          <span className="text-xs text-gray-400 italic">
                            (prorated)
                          </span>
                        )}
                        <ExclamationTriangleIcon className="h-4 w-4 text-red-500 flex-shrink-0" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          'font-mono',
                          r.proratedAdjustment > 0 ? 'text-green-600' : 
                          r.proratedAdjustment < 0 ? 'text-red-600' : 'text-gray-500'
                        )}>
                          {fmtCurrency(r.proratedAdjustment)}
                        </span>
                        {r.isPartialLot && (
                          <span className="text-xs text-gray-400 italic">
                            (prorated)
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
              <tr>
                <td className="px-3 py-2 border-r border-gray-200"></td>
                <td className="px-3 py-2 border-r border-gray-200"></td>
                <td className="px-3 py-2 text-xs text-gray-500 text-right border-r border-gray-200">Total:</td>
                <td className="px-3 py-2 font-semibold text-gray-900 border-r border-gray-200">{data.totalDistance.toFixed(0)}</td>
                <td className="px-3 py-2 text-xs text-gray-500 border-r border-gray-200">
                  <span className="mr-2">Avg:</span>
                  <span className="font-semibold text-gray-900">{data.avgMRI.toFixed(1)}</span>
                </td>
                <td className="pl-5 pr-3 py-2 text-left border-r border-gray-200">
                  {data.totalALRCount > 0 && (
                    <span className="text-xs font-semibold text-red-600">{data.totalALRCount}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className={cn(
                    'font-mono font-semibold',
                    data.totalAdjustment >= 0 ? 'text-green-600' : 'text-red-600'
                  )}>
                    {fmtCurrency(data.totalAdjustment)}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 py-12">
            <p>{issuesOnly ? 'No segments with issues' : 'No segment data available'}</p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span>Ohio DOT Proposal Note 420</span>
          {isReversed && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Reverse Stationing</span>
          )}
        </div>
        <span>
          Thickness: {sched.label} — Correction threshold: &gt;{sched.correctionThreshold} in/mi
        </span>
      </div>
    </div>
  );
}